import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../components/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCreditCard, FaPlus, FaTrash, FaShieldAlt, FaHistory, FaCheckCircle, FaExclamationCircle, FaSyncAlt } from "react-icons/fa";
import { database, functions } from "../../firebase";
import { ref, push, set, onValue, remove, serverTimestamp } from "firebase/database";
import { httpsCallable } from "firebase/functions";

const PaymentManagement = () => {
  const { user, loading } = useAuth(); // isAdmin, isCustomer 제거 (사용하지 않음)
  const navigate = useNavigate();
  const location = useLocation();
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [refreshingIds, setRefreshingIds] = useState(new Set());

  const hasAutoChecked = useRef(false);

  // API 방식 카드 등록 상태 및 관련 state 제거


  useEffect(() => {
    if (!loading && !user) {
      alert("로그인이 필요한 페이지입니다.");
      navigate("/");
    }
  }, [user, loading, navigate]);

  // 카드 등록 핸들러 (useEffect 외부로 정의된 것을 사용하기 위해 useCallback 권장되지만, 여기선 의존성 제거로 처리)
  const handleAddCard = async () => {
    if (!window.PortOne) {
        alert("PortOne SDK가 로드되지 않았습니다.");
        return;
    }

    try {
        const issueId = `issue_${new Date().getTime()}`;
        // PortOne V2: 빌링키 발급 요청 (카드 등록)
        console.log("PortOne Config:", {
            storeId: process.env.REACT_APP_PORTONE_STORE_ID,
            channelKey: process.env.REACT_APP_PORTONE_CHANNEL_KEY
        });
        const response = await window.PortOne.requestIssueBillingKey({
            storeId: process.env.REACT_APP_PORTONE_STORE_ID, // 고객사 스토어 아이디
            channelKey: process.env.REACT_APP_PORTONE_CHANNEL_KEY, // 채널 키 (KG이니시스)
            billingKeyMethod: "CARD", // 결제 수단
            issueId: issueId, // 고유 ID
            issueName: "카드 등록 (Workway)", 
            customer: {
                customerId: `user_${user?.uid || "guest"}`,
                fullName: user?.displayName || "홍길동",
                phoneNumber: "010-1234-5678",
                email: user?.email,
            },
            windowType: {
                pc: "IFRAME",
                mobile: "POPUP",
            },
        });
        
        console.log("PortOne Response:", response); // 디버깅용 로그

        if (response.code != null) {
            // 오류 발생
            alert(`카드 등록 실패: ${response.message}`);
            return;
        }

        // 성공 시 로직
        alert("카드가 성공적으로 등록되었습니다.");
        
        // 포트원 응답에서 빌링키 추출
        const billingKey = response.billingKey || response.issueId || issueId;

        // 서버(Firebase Functions)를 통해 카드 상세 정보 조회
        let cardName = "신규 등록 카드";
        let cardNumber = "****-****-****-****";
        let cardType = "기타"; // 기본값

        try {
            // 결제 연동 후 서버에서 상세 정보를 조회합니다.
            console.log("DEBUG: 서버에 정보 조회 요청 시작. billingKey:", billingKey);
            
            const infoResponse = await fetch("https://us-central1-workway-5f56c.cloudfunctions.net/getBillingKeyInfo", { 
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ billingKey })
            });

            if (infoResponse.ok) {
                const infoData = await infoResponse.json();
                console.log("DEBUG: 서버에서 받은 데이터:", infoData);
                
                const methodInfo = infoData.methods?.[0] || {};
                const card = methodInfo.card || {};
                
                cardName = card.name || card.issuer || "조회된 카드명 없음";
                cardNumber = card.number || "****-****-****-****";
                
                // ownerType에 따라 법인/개인 구분 (CORPORATE / PERSONAL)
                if (card.ownerType === "CORPORATE") {
                    cardType = "법인";
                } else if (card.ownerType === "PERSONAL") {
                    cardType = "개인";
                } else {
                    cardType = "카드"; // 그 외의 경우
                }
                
                console.log(`추출 정보: ${cardName} / ${cardNumber} / ${cardType}`);
            } else {
                const errText = await infoResponse.text();
                console.error("서버 응답 에러:", infoResponse.status, errText);
            }
        } catch (infoError) {
            console.error("카드 정보 조회 네트워크 오류:", infoError);
        }

        // Firebase에 저장 (서버에서 가져온 상세 정보 포함)
        if (user) {
            const newCardRef = push(ref(database, `users/${user.uid}/billingKeys`));
            await set(newCardRef, {
                name: cardName,
                number: cardNumber,
                type: cardType, // 추출된 법인/개인 정보 저장
                billingKey: billingKey,
                issueId: issueId,
                createdAt: new Date().toISOString(),
                pgResponse: response 
            });
            // 관리자에게 카드 등록 알림 이메일 전송
            try {
                const emailUrl = process.env.REACT_APP_EMAIL_API_URL || "https://us-central1-workway-5f56c.cloudfunctions.net/sendSupportTicketEmail";
                const emailKey = process.env.REACT_APP_EMAIL_API_KEY || "om8B2QikaJvIImlhlXTw";
                
                await fetch(emailUrl, {
                    method: "POST",
                    headers: { 
                        "Content-Type": "application/json",
                        "X-API-Key": emailKey
                    },
                    body: JSON.stringify({
                        to: "hms@workway.co.kr",
                        name: user.name || user.displayName || "사용자",
                        subject: `[카드등록] ${user.name || user.displayName}님이 새로운 카드를 등록했습니다.`,
                        ticketTitle: "카드 등록 알림",
                        cardName: cardName,
                        cardNumber: cardNumber,
                        emailType: "card_registration"
                    })
                });
            } catch (emailError) {
                console.error("알림 이메일 전송 실패:", emailError);
            }
        }

    } catch (error) {
        console.error("PortOne V2 Error:", error);
        alert(`결제 연동 중 오류가 발생했습니다: ${error.message || JSON.stringify(error)}`);
    }
  };

  // API 방식 카드 등록 핸들러


  useEffect(() => {
    if (user) {
      // Firebase에서 사용자의 카드 목록 실시간 수신
      const cardsRef = ref(database, `users/${user.uid}/billingKeys`);
      const unsubscribe = onValue(cardsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const loadedCards = Object.entries(data).map(([key, value]) => ({
            id: key,
            ...value,
          }));
          setCards(loadedCards);
        } else {
          setCards([]);
        }
        setCardsLoading(false);
      });

      // 결제 내역 가져오기
      const historyRef = ref(database, "payment_history");
      const unsubscribeHistory = onValue(historyRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const userHistory = Object.entries(data)
            .map(([id, val]) => ({ id, ...val }))
            .filter(item => item.uid === user.uid) // 본인 것만 필터링
            .sort((a, b) => b.timestamp - a.timestamp);
          setHistory(userHistory);
        }
      });

      return () => {
        unsubscribe();
        unsubscribeHistory();
      };
    }
  }, [user, navigate]);

  // 로딩 시 자동 상태 체크 로직
  useEffect(() => {
    if (history.length > 0 && !hasAutoChecked.current) {
      hasAutoChecked.current = true;
      // 최근 5건 중 취소되지 않은 건들에 대해 자동 상태 확인 실행
      const itemsToAutoCheck = history
        .slice(0, 5)
        .filter(item => item.status !== 'CANCELLED');
      
      itemsToAutoCheck.forEach(item => {
        refreshStatus(item, true); // true: 알림창 띄우지 않는 silent 모드
      });
    }

    // Pricing 페이지에서 넘어온 경우 카드 등록 안내
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (location.state?.fromPricing && !cardsLoading && cards.length === 0) {
      const planName = location.state.selectedPlan?.name || "선택하신 플랜";
      if (window.confirm(`${planName} 구독을 위해 결제 카드를 먼저 등록하시겠습니까?`)) {
        handleAddCard();
      }
      // state 초기화 (새로고침 시 다시 뜨지 않도록)
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [history, location.state, cards.length, cardsLoading]); // handleAddCard는 의서성에서 제외하거나 useCallback 사용 필요 (여기선 disable 처리)

  const handleRemoveCard = async (id) => {
    if (window.confirm("정말로 이 결제 수단을 삭제하시겠습니까?")) {
      if (user) {
        try {
            await remove(ref(database, `users/${user.uid}/billingKeys/${id}`));
            alert("삭제되었습니다.");
        } catch (error) {
            console.error("삭제 실패:", error);
            alert("삭제 중 오류가 발생했습니다.");
        }
      }
    }
  };

  const refreshStatus = async (item, silent = false) => {
    setRefreshingIds(prev => new Set(prev).add(item.id));
    try {
      const getInfoFn = httpsCallable(functions, "getPaymentInfo");
      const result = await getInfoFn({ paymentId: item.paymentId });
      const portoneData = result.data.data;

      // DB 상태와 포트원 상태가 다를 때만 업데이트
      if (item.status !== portoneData.status) {
        const statusRef = ref(database, `payment_history/${item.id}`);
        await set(statusRef, {
          ...item,
          status: portoneData.status,
          cancellationReason: portoneData.cancellations?.[0]?.reason || null,
          updatedAt: serverTimestamp()
        });
        if (!silent) alert(`결제 상태가 업데이트되었습니다: ${portoneData.status}`);
      } else {
        if (!silent) alert(`현재 상태(${portoneData.status})가 최신입니다.`);
      }
    } catch (error) {
      console.error("Refresh Status Error:", error);
      if (!silent) alert("상태 업데이트 실패: " + error.message);
    } finally {
      setRefreshingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };



  return (
    <div className="bg-gray-50 min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900">결제 관리</h1>
          <p className="mt-2 text-gray-600">
            서비스 이용을 위한 결제 수단을 등록하고 관리하세요.
          </p>
        </div>

        {/* Card List Section */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 sm:p-8 bg-gradient-to-r from-blue-600 to-indigo-700 text-white flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold flex items-center text-white">
                        <FaCreditCard className="mr-3" /> 등록된 결제 수단
                    </h2>
                    <p className="text-blue-100 text-sm mt-1">
                        정기 결제에 사용될 기본 결제 수단을 관리합니다.
                    </p>
                </div>
                <FaShieldAlt className="text-4xl text-blue-400 opacity-50" />
            </div>

            <div className="p-6 sm:p-8">
                {cards.length === 0 ? (
                    <div className="text-center py-6">
                        <div className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 mb-8 p-12">
                            <FaCreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900">등록된 카드가 없습니다</h3>
                            <p className="mt-1 text-gray-500">서비스 이용을 위해 카드를 등록해주세요.</p>
                        </div>
                        
                        {/* Always show the API form when no cards are present */}
                        <button 
                            onClick={handleAddCard}
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <FaPlus className="mr-2" /> 카드 등록하기 (결제창)
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {cards.map((card) => (
                            <div key={card.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:shadow-md transition-shadow bg-white">
                                <div className="flex items-center">
                                    <div className="h-12 w-16 bg-gray-100 rounded-md flex items-center justify-center mr-4">
                                        <FaCreditCard className="text-gray-500 text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-gray-900">{card.name}</h4>
                                        <p className="text-gray-500 text-sm">{card.number} <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{card.type}</span></p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleRemoveCard(card.id)}
                                    className="text-gray-400 hover:text-red-500 p-2 transition-colors"
                                    title="카드 삭제"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        ))}
                        
                        <div className="mt-10">
                            <div className="flex justify-center mb-6">
                                <button 
                                    onClick={handleAddCard}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                   새 카드 추가하기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 flex items-center justify-center">
                    <FaShieldAlt className="mr-2" />
                    안전한 결제를 위해 카드 정보는 암호화되어 PG사에 저장됩니다.
                </p>
            </div>
        </div>

        {/* Payment History Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-gray-900 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center">
              <FaHistory className="mr-3 text-blue-400" /> 결제 이용 내역
            </h2>
            <span className="text-xs text-gray-400 font-medium">서비스 청구 및 결제된 기록입니다.</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">결제 일시</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">항목</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-widest">금액</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-widest">상태</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-20 text-center text-gray-400">
                      결제 내역이 아직 없습니다.
                    </td>
                  </tr>
                ) : (
                  history.map((item) => (
                    <tr key={item.id} className="hover:bg-blue-50/30 transition-colors">
                      <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500">
                        {item.timestamp ? new Date(item.timestamp).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-sm font-bold text-gray-900">{item.orderName}</div>
                        <div className="text-xs text-gray-400">{item.cardName} ({item.cardNumber?.substring(item.cardNumber.length - 4)})</div>
                      </td>
                      <td className="px-6 py-5 text-right whitespace-nowrap text-sm font-black text-gray-900">
                        {item.amount.toLocaleString()}원
                      </td>
                      <td className="px-6 py-5 text-center whitespace-nowrap">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-black uppercase tracking-tighter ${
                            item.status === 'PAID' || item.status === 'SUCCESS' 
                              ? 'bg-green-100 text-green-700' 
                              : item.status === 'CANCELLED'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {item.status === 'SUCCESS' || item.status === 'PAID' ? <FaCheckCircle className="mr-1" /> : null}
                            {item.status === 'CANCELLED' ? <FaExclamationCircle className="mr-1" /> : null}
                            {item.status}
                          </span>
                          <button 
                            onClick={() => refreshStatus(item)}
                            disabled={refreshingIds.has(item.id)}
                            className="text-[10px] text-blue-500 hover:text-blue-700 flex items-center font-bold"
                          >
                            <FaSyncAlt className={`mr-1 ${refreshingIds.has(item.id) ? 'animate-spin' : ''}`} />
                            {refreshingIds.has(item.id) ? "갱신중" : "상태갱신"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      


    </div>
    </div>
  );
};

export default PaymentManagement;
